/**
 * Gate Wallet Adapter for Solana
 *
 * Custom wallet adapter that integrates Gate Wallet with AppKit.
 * Detects Gate Wallet browser extension via window.gatewallet or window.solana.isGateWallet
 */

import {
  BaseMessageSignerWalletAdapter,
  WalletConnectionError,
  WalletDisconnectedError,
  WalletDisconnectionError,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletPublicKeyError,
  WalletReadyState,
  WalletSignMessageError,
  WalletSignTransactionError,
  scopePollingDetectionStrategy,
} from '@solana/wallet-adapter-base';
import type { WalletName } from '@solana/wallet-adapter-base';
import type { SendOptions } from '@solana/web3.js';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';

interface GateWalletPublicKey {
  toBytes?(): Uint8Array;
  toString?(): string;
}

interface GateWalletProvider {
  isGateWallet?: boolean;
  publicKey?: GateWalletPublicKey | string;
  isConnected?: boolean;
  connect(): Promise<{ publicKey?: GateWalletPublicKey | string } | void>;
  disconnect(): Promise<void>;
  signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T>;
  signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]>;
  signMessage(message: Uint8Array): Promise<{ signature: Uint8Array }>;
  signAndSendTransaction<T extends Transaction | VersionedTransaction>(
    transaction: T,
    options?: SendOptions
  ): Promise<{ signature: string }>;
  on(event: string, callback: (...args: any[]) => void): void;
  off(event: string, callback: (...args: any[]) => void): void;
}

interface GateWalletWindow extends Window {
  gatewallet?: GateWalletProvider;
  solana?: GateWalletProvider & { isGateWallet?: boolean };
}

declare const window: GateWalletWindow;

export const GateWalletName = 'Gate Wallet' as WalletName<'Gate Wallet'>;

export class GateWalletAdapter extends BaseMessageSignerWalletAdapter {
  name = GateWalletName;
  url = 'https://www.gate.io/web3';
  icon =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiByeD0iNCIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyXzEyM180NTYpIi8+CjxwYXRoIGQ9Ik0xMiA2QzguNjg2MjkgNiA2IDguNjg2MjkgNiAxMkM2IDE1LjMxMzcgOC42ODYyOSAxOCAxMiAxOEMxNS4zMTM3IDE4IDE4IDE1LjMxMzcgMTggMTJIMTJWNloiIGZpbGw9IndoaXRlIi8+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9InBhaW50MF9saW5lYXJfMTIzXzQ1NiIgeDE9IjAiIHkxPSIwIiB4Mj0iMjQiIHkyPSIyNCIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPgo8c3RvcCBzdG9wLWNvbG9yPSIjMDA2OEZGIi8+CjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iIzE3RTZBMSIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo=';

  readonly supportedTransactionVersions = new Set(['legacy', 0] as const);

  private _connecting: boolean;
  private _wallet: GateWalletProvider | null;
  private _publicKey: PublicKey | null;
  private _readyState: WalletReadyState =
    typeof window === 'undefined' || typeof document === 'undefined'
      ? WalletReadyState.Unsupported
      : WalletReadyState.NotDetected;

  constructor() {
    super();
    this._connecting = false;
    this._wallet = null;
    this._publicKey = null;

    if (this._readyState !== WalletReadyState.Unsupported) {
      scopePollingDetectionStrategy(() => {
        const wallet = this._getProvider();
        if (wallet) {
          this._readyState = WalletReadyState.Installed;
          this.emit('readyStateChange', this._readyState);
          return true;
        }
        return false;
      });
    }
  }

  get publicKey(): PublicKey | null {
    return this._publicKey;
  }

  get connecting(): boolean {
    return this._connecting;
  }

  get connected(): boolean {
    // Check both wallet.isConnected and our internal _publicKey
    // Some wallets (like Gate) may not set isConnected after connect()
    return !!(this._wallet && this._publicKey);
  }

  get readyState(): WalletReadyState {
    return this._readyState;
  }

  private _getProvider(): GateWalletProvider | null {
    if (typeof window === 'undefined') return null;

    // Check for Gate Wallet specific provider
    if (window.gatewallet) {
      return window.gatewallet;
    }

    // Check if window.solana is Gate Wallet
    if (window.solana?.isGateWallet) {
      return window.solana;
    }

    return null;
  }

  async connect(): Promise<void> {
    try {
      if (this.connected || this.connecting) return;
      if (this._readyState !== WalletReadyState.Installed) throw new WalletNotReadyError();

      this._connecting = true;

      const wallet = this._getProvider();
      if (!wallet) throw new WalletNotReadyError();

      try {
        const response = await wallet.connect();

        // Debug: Log what Gate Wallet returns
        console.log('[GateWalletAdapter] connect response:', response);
        console.log('[GateWalletAdapter] response type:', typeof response);
        console.log('[GateWalletAdapter] wallet.publicKey after connect:', wallet.publicKey);
        if (response) {
          const respKeys = Object.keys(response);
          console.log('[GateWalletAdapter] response keys:', respKeys);
          console.log('[GateWalletAdapter] response.publicKey:', (response as any).publicKey);
          // Log each key's value to understand what Gate Wallet provides
          respKeys.forEach((key) => {
            console.log(`[GateWalletAdapter] response.${key}:`, (response as any)[key]);
          });
        }

        // Handle different public key formats from Gate Wallet
        let pubKey: PublicKey | null = null;

        // Try getting from response first
        const responsePubKey = (response as any)?.publicKey;
        if (responsePubKey) {
          console.log('[GateWalletAdapter] responsePubKey type:', typeof responsePubKey);
          console.log('[GateWalletAdapter] responsePubKey value:', responsePubKey);
          if (typeof responsePubKey === 'string') {
            pubKey = new PublicKey(responsePubKey);
          } else if (typeof responsePubKey.toBytes === 'function') {
            pubKey = new PublicKey(responsePubKey.toBytes());
          } else if (typeof responsePubKey.toString === 'function') {
            pubKey = new PublicKey(responsePubKey.toString());
          }
        }

        // Fallback: try getting from wallet object after connect
        if (!pubKey && wallet.publicKey) {
          const walletPubKey = wallet.publicKey;
          console.log('[GateWalletAdapter] walletPubKey type:', typeof walletPubKey);
          console.log('[GateWalletAdapter] walletPubKey value:', walletPubKey);
          if (typeof walletPubKey === 'string') {
            pubKey = new PublicKey(walletPubKey);
          } else if (typeof walletPubKey.toBytes === 'function') {
            pubKey = new PublicKey(walletPubKey.toBytes());
          } else if (typeof walletPubKey.toString === 'function') {
            pubKey = new PublicKey(walletPubKey.toString());
          }
        }

        // Additional fallback: check if response itself is a public key string
        if (!pubKey && typeof response === 'string') {
          console.log('[GateWalletAdapter] response is string, trying as pubkey');
          pubKey = new PublicKey(response);
        }

        // Additional fallback: check window.gatewallet.publicKey or window.solana.publicKey directly
        // IMPORTANT: Only use window.solana if it's confirmed to be Gate Wallet (not Phantom or other wallets)
        if (!pubKey) {
          const gw = (window as any).gatewallet;
          const sol = (window as any).solana;
          console.log('[GateWalletAdapter] Checking window.gatewallet.publicKey:', gw?.publicKey);
          console.log('[GateWalletAdapter] Checking window.solana.isGateWallet:', sol?.isGateWallet);
          console.log('[GateWalletAdapter] Checking window.solana.publicKey:', sol?.publicKey);

          // Only use window.gatewallet, or window.solana if it's confirmed to be Gate Wallet
          const directPubKey = gw?.publicKey || (sol?.isGateWallet ? sol?.publicKey : null);
          if (directPubKey) {
            // Check if it's already a PublicKey instance (has _bn property)
            if (directPubKey._bn !== undefined) {
              console.log('[GateWalletAdapter] Found PublicKey with _bn, using toBase58()');
              pubKey = new PublicKey(directPubKey.toBase58());
            } else if (typeof directPubKey === 'string') {
              pubKey = new PublicKey(directPubKey);
            } else if (typeof directPubKey.toBase58 === 'function') {
              pubKey = new PublicKey(directPubKey.toBase58());
            } else if (typeof directPubKey.toBytes === 'function') {
              pubKey = new PublicKey(directPubKey.toBytes());
            } else if (typeof directPubKey.toString === 'function') {
              pubKey = new PublicKey(directPubKey.toString());
            }
          }
        }

        // Gate Wallet specific: check accountNetworkArr for Solana address
        if (!pubKey && response && (response as any).accountNetworkArr) {
          const networks = (response as any).accountNetworkArr as any[];
          console.log('[GateWalletAdapter] Checking accountNetworkArr:', JSON.stringify(networks, null, 2));
          // Log each network entry for debugging
          networks.forEach((n: any, i: number) => {
            console.log(`[GateWalletAdapter] Network[${i}]:`, JSON.stringify(n));
          });
          // Find Solana network entry - check multiple possible field names and values
          // Gate Wallet uses "SOL" as the network identifier
          const solanaNetwork = networks.find(
            (n: any) =>
              n.network === 'SOL' ||
              n.network === 'solana' ||
              n.network === 'SOLANA' ||
              n.chainId === 'solana' ||
              n.chainId === 'SOLANA' ||
              n.chain === 'solana' ||
              n.chain === 'SOLANA' ||
              n.symbol === 'SOL' ||
              n.coin === 'SOL' ||
              n.coinSymbol === 'SOL' ||
              (typeof n.network === 'string' && n.network.toLowerCase().includes('solana')) ||
              (typeof n.chain === 'string' && n.chain.toLowerCase().includes('solana'))
          );
          if (solanaNetwork) {
            console.log('[GateWalletAdapter] Found Solana network entry:', JSON.stringify(solanaNetwork));
            // Try multiple possible address field names
            const address = solanaNetwork.address || solanaNetwork.addr || solanaNetwork.publicKey || solanaNetwork.wallet;
            if (address) {
              console.log('[GateWalletAdapter] Found Solana address in accountNetworkArr:', address);
              pubKey = new PublicKey(address);
            }
          }
        }

        if (!pubKey) {
          // Provide detailed error for debugging
          const responseKeys = response ? Object.keys(response) : [];
          const gwInstalled = !!(window as any).gatewallet;
          const solIsGate = !!(window as any).solana?.isGateWallet;
          throw new Error(
            `Unable to get public key from Gate Wallet. ` +
            `Response keys: [${responseKeys.join(', ')}]. ` +
            `window.gatewallet exists: ${gwInstalled}. ` +
            `window.solana.isGateWallet: ${solIsGate}. ` +
            `Please ensure Gate Wallet is properly installed and connected.`
          );
        }

        this._publicKey = pubKey;
      } catch (error: any) {
        throw new WalletConnectionError(error?.message, error);
      }

      this._wallet = wallet;

      // Set up event listeners
      wallet.on('disconnect', this._disconnected);
      wallet.on('accountChanged', this._accountChanged);

      this.emit('connect', this._publicKey);
    } catch (error: any) {
      this.emit('error', error);
      throw error;
    } finally {
      this._connecting = false;
    }
  }

  async disconnect(): Promise<void> {
    const wallet = this._wallet;
    if (wallet) {
      wallet.off('disconnect', this._disconnected);
      wallet.off('accountChanged', this._accountChanged);

      this._wallet = null;
      this._publicKey = null;

      try {
        await wallet.disconnect();
      } catch (error: any) {
        this.emit('error', new WalletDisconnectionError(error?.message, error));
      }
    }

    this.emit('disconnect');
  }

  /**
   * Helper to restore original signatures to a signed transaction.
   * Gate Wallet may return new transaction objects without the backend's partial signature.
   */
  private _restoreSignatures<T extends Transaction | VersionedTransaction>(
    signedTx: T,
    originalSigs: { pubkey: PublicKey; signature: Buffer | null }[]
  ): T {
    if (signedTx instanceof Transaction) {
      for (const origSig of originalSigs) {
        if (!origSig.signature) continue;

        // Check if this signature is missing from the signed transaction
        const existingSig = signedTx.signatures.find(
          s => s.publicKey.equals(origSig.pubkey)
        );

        if (existingSig && !existingSig.signature) {
          // The signed transaction has this signer but no signature - add the original
          console.log(`[GateWalletAdapter] Restoring signature for ${origSig.pubkey.toBase58().slice(0, 8)}...`);
          existingSig.signature = origSig.signature;
        } else if (!existingSig) {
          // The signed transaction doesn't have this signer - log for debugging
          console.warn(`[GateWalletAdapter] Signed tx missing signer ${origSig.pubkey.toBase58().slice(0, 8)}...`);
        }
      }
    }
    return signedTx;
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    try {
      const wallet = this._wallet;
      if (!wallet) throw new WalletNotConnectedError();

      // Store original signatures for restoration after signing
      const originalSigs: { pubkey: PublicKey; signature: Buffer | null }[] = [];
      if (transaction instanceof Transaction) {
        for (const sig of transaction.signatures) {
          if (sig.signature) {
            originalSigs.push({ pubkey: sig.publicKey, signature: sig.signature });
          }
        }
      }

      const gateWallet = (window as any).gatewallet;
      console.log('[GateWalletAdapter] signTransaction called');
      console.log('[GateWalletAdapter] wallet.signTransaction:', typeof wallet.signTransaction);

      // Log ALL available methods on gateWallet to find signing methods
      if (gateWallet) {
        const allMethods = Object.getOwnPropertyNames(Object.getPrototypeOf(gateWallet) || {})
          .concat(Object.keys(gateWallet));
        console.log('[GateWalletAdapter] All gateWallet methods:', allMethods.join(', '));

        // Check for any method containing 'sign' or 'transaction'
        const signMethods = allMethods.filter(m =>
          m.toLowerCase().includes('sign') ||
          m.toLowerCase().includes('transaction') ||
          m.toLowerCase().includes('solana')
        );
        console.log('[GateWalletAdapter] Sign-related methods:', signMethods.join(', '));
      }

      try {
        // Prefer Gate Wallet's Solana sub-provider if present.
        // Gate injects a multi-chain provider at window.gatewallet, and exposes Solana signing under gatewallet.solana.
        const solanaProvider =
          (gateWallet as any)?.solana || ((window as any).solana?.isGateWallet ? (window as any).solana : null);

        if (solanaProvider && typeof solanaProvider.signTransaction === 'function') {
          // Ensure the Solana provider is connected (some Gate builds keep root + chain providers separate)
          if (typeof solanaProvider.connect === 'function' && solanaProvider.isConnected !== true) {
            await solanaProvider.connect();
          }

          // Gate's injected Solana provider sometimes expects `request({ method: 'signTransaction', params })`
          // with a serialized transaction, rather than receiving a Transaction class instance directly.
          if (typeof (solanaProvider as any).request === 'function') {
            try {
              // Serialize transaction to base64 (handle both legacy and versioned transactions)
              let serializedTxBytes: Uint8Array;
              if ('version' in (transaction as any)) {
                serializedTxBytes = (transaction as any).serialize();
              } else {
                serializedTxBytes = (transaction as any).serialize({
                  requireAllSignatures: false,
                  verifySignatures: false,
                });
              }
              const serializedTxBase64 = Buffer.from(serializedTxBytes).toString('base64');

              const requestFormats: any[] = [
                { method: 'signTransaction', params: { transaction: serializedTxBase64, encoding: 'base64' } },
                { method: 'signTransaction', params: { transaction: serializedTxBase64 } },
                { method: 'solana_signTransaction', params: { transaction: serializedTxBase64 } },
                { method: 'solana_signTransaction', params: { message: serializedTxBase64 } },
                { method: 'signTransaction', params: serializedTxBase64 },
              ];

              for (let i = 0; i < requestFormats.length; i++) {
                const fmt = requestFormats[i];
                try {
                  const response = await (solanaProvider as any).request(fmt);
                  if (!response) continue;

                  // If response is already a Transaction-like object
                  if (typeof (response as any)?.serialize === 'function') {
                    return this._restoreSignatures(response as T, originalSigs);
                  }

                  // If response contains a signed transaction string
                  const signedTxData =
                    (response as any)?.signedTransaction ||
                    (response as any)?.signedTx ||
                    (response as any)?.transaction ||
                    (response as any)?.signature;
                  if (typeof signedTxData === 'string') {
                    const buf = Buffer.from(signedTxData, 'base64');
                    if ('version' in (transaction as any)) {
                      return this._restoreSignatures(VersionedTransaction.deserialize(buf) as T, originalSigs);
                    } else {
                      return this._restoreSignatures(Transaction.from(buf) as T, originalSigs);
                    }
                  }

                  // If response is a raw base64 string
                  if (typeof response === 'string') {
                    const buf = Buffer.from(response, 'base64');
                    if ('version' in (transaction as any)) {
                      return this._restoreSignatures(VersionedTransaction.deserialize(buf) as T, originalSigs);
                    } else {
                      return this._restoreSignatures(Transaction.from(buf) as T, originalSigs);
                    }
                  }
                } catch {
                  // Try next format
                }
              }
            } catch {
              // Fall through to direct signTransaction below
            }
          }

          const signed = await solanaProvider.signTransaction(transaction);
          return this._restoreSignatures(signed as T, originalSigs);
        }

        // Try direct wallet.signTransaction first
        if (typeof wallet.signTransaction === 'function') {
          console.log('[GateWalletAdapter] Using wallet.signTransaction...');
          const signed = await wallet.signTransaction(transaction);
          return this._restoreSignatures(signed, originalSigs);
        }

        // Serialize transaction to base64 (handle both legacy and versioned transactions)
        let serializedTx: string;
        let serializedTxBytes: Uint8Array;
        if ('version' in transaction) {
          serializedTxBytes = transaction.serialize();
          serializedTx = Buffer.from(serializedTxBytes).toString('base64');
        } else {
          serializedTxBytes = transaction.serialize({ requireAllSignatures: false });
          serializedTx = Buffer.from(serializedTxBytes).toString('base64');
        }
        const serializedTxHex = Buffer.from(serializedTxBytes).toString('hex');

        // Try calling signTransaction directly on gateWallet (not through request)
        if (gateWallet && typeof gateWallet.signTransaction === 'function') {
          console.log('[GateWalletAdapter] Trying gateWallet.signTransaction directly...');
          try {
            const response = await gateWallet.signTransaction(transaction);
            console.log('[GateWalletAdapter] gateWallet.signTransaction response:', response);
            if (response) {
              return this._restoreSignatures(response as T, originalSigs);
            }
          } catch (e: any) {
            console.log('[GateWalletAdapter] gateWallet.signTransaction failed:', e?.message);
          }
        }

        // Try with serialized transaction
        if (gateWallet && typeof gateWallet.signTransaction === 'function') {
          console.log('[GateWalletAdapter] Trying gateWallet.signTransaction with serialized tx...');
          try {
            const response = await gateWallet.signTransaction(serializedTx);
            console.log('[GateWalletAdapter] Response:', response);
            if (response) {
              if (typeof response === 'string') {
                const signedTxBuffer = Buffer.from(response, 'base64');
                if ('version' in transaction) {
                  return this._restoreSignatures(VersionedTransaction.deserialize(signedTxBuffer) as T, originalSigs);
                } else {
                  return this._restoreSignatures(Transaction.from(signedTxBuffer) as T, originalSigs);
                }
              }
              return this._restoreSignatures(response as T, originalSigs);
            }
          } catch (e: any) {
            console.log('[GateWalletAdapter] Failed:', e?.message);
          }
        }

        // Try solana-specific methods that might exist
        const solanaMethodNames = ['signSolanaTransaction', 'solanaTx', 'signSolanaTx'];
        for (const methodName of solanaMethodNames) {
          if (gateWallet && typeof gateWallet[methodName] === 'function') {
            console.log(`[GateWalletAdapter] Trying gateWallet.${methodName}...`);
            try {
              const response = await gateWallet[methodName](transaction);
              console.log(`[GateWalletAdapter] ${methodName} response:`, response);
              if (response) {
                return this._restoreSignatures(response as T, originalSigs);
              }
            } catch (e: any) {
              console.log(`[GateWalletAdapter] ${methodName} failed:`, e?.message);
            }
          }
        }

        // Try window.gatewallet.request for signing with many format variations
        if (gateWallet && typeof gateWallet.request === 'function') {
          console.log('[GateWalletAdapter] Using gateWallet.request for signTransaction...');

          const requestFormats = [
            // Standard Solana wallet adapter format
            { method: 'signTransaction', params: { transaction: serializedTx, network: 'SOL' } },
            { method: 'signTransaction', params: { transaction: serializedTxBytes, network: 'SOL' } },
            { method: 'solana_signTransaction', params: { transaction: serializedTx } },
            { method: 'solana_signTransaction', params: { message: serializedTx } },
            // With pubkey
            { method: 'signTransaction', params: { transaction: serializedTx, network: 'SOL', pubkey: this._publicKey?.toBase58() } },
            // Hex format
            { method: 'signTransaction', params: { transaction: serializedTxHex, network: 'SOL', encoding: 'hex' } },
            // Array format
            { method: 'signTransaction', params: { transaction: Array.from(serializedTxBytes), network: 'SOL' } },
            // Minimal format
            { method: 'signTransaction', params: serializedTx },
            // With chain specification
            { method: 'signTransaction', params: { transaction: serializedTx, chain: 'solana' } },
            { method: 'signTransaction', params: { transaction: serializedTx, chainId: 'solana' } },
          ];

          for (let i = 0; i < requestFormats.length; i++) {
            const format = requestFormats[i];
            console.log(`[GateWalletAdapter] Trying signTransaction format ${i + 1}:`, JSON.stringify(format).slice(0, 100));
            try {
              const response = await gateWallet.request(format);
              console.log(`[GateWalletAdapter] signTransaction format ${i + 1} response:`, response);

              if (response) {
                // Response might be a signed transaction or signature
                if (response.signature || response.signedTransaction) {
                  const signedTxData = response.signedTransaction || response.signature;
                  if (typeof signedTxData === 'string') {
                    const signedTxBuffer = Buffer.from(signedTxData, 'base64');
                    if ('version' in transaction) {
                      return this._restoreSignatures(VersionedTransaction.deserialize(signedTxBuffer) as T, originalSigs);
                    } else {
                      return this._restoreSignatures(Transaction.from(signedTxBuffer) as T, originalSigs);
                    }
                  }
                }
                if (response.serialize) {
                  return this._restoreSignatures(response as T, originalSigs);
                }
                // If response is a string (base64 tx)
                if (typeof response === 'string') {
                  const signedTxBuffer = Buffer.from(response, 'base64');
                  if ('version' in transaction) {
                    return this._restoreSignatures(VersionedTransaction.deserialize(signedTxBuffer) as T, originalSigs);
                  } else {
                    return this._restoreSignatures(Transaction.from(signedTxBuffer) as T, originalSigs);
                  }
                }
              }
            } catch (reqError: any) {
              console.log(`[GateWalletAdapter] signTransaction format ${i + 1} failed:`, reqError?.message, 'code:', reqError?.code);
              // Don't throw on 4001 immediately - try all formats first
              // Only throw if this is the last format and all failed
            }
          }
        }

        // If we get here, Gate Wallet doesn't support Solana transaction signing
        // The blank popup issue indicates Gate Wallet's Solana dApp signing UI is incomplete
        throw new Error(
          'Gate Wallet\'s Solana transaction signing is not fully supported. ' +
          'While Gate Wallet can display your Solana address, its signing UI for Solana dApps appears incomplete (blank popup). ' +
          'For transactions, please disconnect and use a Solana-native wallet like Phantom, Solflare, or Backpack. ' +
          'You can still use Gate Wallet to view your address and track your campaign bonus.'
        );
      } catch (error: any) {
        throw new WalletSignTransactionError(error?.message, error);
      }
    } catch (error: any) {
      this.emit('error', error);
      throw error;
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    try {
      const wallet = this._wallet;
      if (!wallet) throw new WalletNotConnectedError();

      console.log('[GateWalletAdapter] signAllTransactions called, count:', transactions.length);

      // Store original signatures from input transactions (for partially-signed transactions from backend)
      // We need to preserve these in case Gate Wallet returns new transaction objects without them
      const originalSignaturesList: { pubkey: PublicKey; signature: Buffer | null }[][] = [];
      for (const tx of transactions) {
        if (tx instanceof Transaction) {
          const sigs: { pubkey: PublicKey; signature: Buffer | null }[] = [];
          for (const sig of tx.signatures) {
            if (sig.signature) {
              sigs.push({ pubkey: sig.publicKey, signature: sig.signature });
            }
          }
          originalSignaturesList.push(sigs);
        } else {
          originalSignaturesList.push([]);
        }
      }

      try {
        let signedTransactions: T[];

        // Try direct wallet.signAllTransactions first
        if (typeof wallet.signAllTransactions === 'function') {
          console.log('[GateWalletAdapter] Using wallet.signAllTransactions...');
          signedTransactions = await wallet.signAllTransactions(transactions);
        } else {
          // Fall back to signing each transaction individually
          // Note: signTransaction already restores signatures, so no need to do it again
          console.log('[GateWalletAdapter] Falling back to signing transactions individually...');
          signedTransactions = [];
          for (const tx of transactions) {
            const signed = await this.signTransaction(tx);
            signedTransactions.push(signed);
          }
          // Return early since signTransaction already restored signatures
          return signedTransactions;
        }

        // Restore original signatures for wallet.signAllTransactions path
        for (let i = 0; i < signedTransactions.length; i++) {
          this._restoreSignatures(signedTransactions[i], originalSignaturesList[i]);
        }

        return signedTransactions;
      } catch (error: any) {
        throw new WalletSignTransactionError(error?.message, error);
      }
    } catch (error: any) {
      this.emit('error', error);
      throw error;
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    try {
      const wallet = this._wallet;
      if (!wallet) throw new WalletNotConnectedError();

      // Debug: Log available methods on the wallet provider
      const walletMethods = Object.keys(wallet);
      console.log('[GateWalletAdapter] Wallet provider methods:', walletMethods);
      console.log('[GateWalletAdapter] All methods:', walletMethods.join(', '));
      console.log('[GateWalletAdapter] Has signMessage:', typeof (wallet as any).signMessage);
      console.log('[GateWalletAdapter] Has request:', typeof (wallet as any).request);
      console.log('[GateWalletAdapter] Has sign:', typeof (wallet as any).sign);
      console.log('[GateWalletAdapter] Message to sign:', message);
      console.log('[GateWalletAdapter] Message as string:', new TextDecoder().decode(message));
      console.log('[GateWalletAdapter] Our publicKey:', this._publicKey?.toBase58());

      const gateWallet = (window as any).gatewallet;

      // Log all methods on window.gatewallet to understand the API
      if (gateWallet) {
        const gwMethods = Object.keys(gateWallet);
        console.log('[GateWalletAdapter] window.gatewallet methods:', gwMethods.join(', '));
        // Check for Solana-specific provider
        console.log('[GateWalletAdapter] gateWallet.solana:', gateWallet.solana);
        console.log('[GateWalletAdapter] gateWallet.getSolanaProvider:', typeof gateWallet.getSolanaProvider);
        console.log('[GateWalletAdapter] gateWallet.getProvider:', typeof gateWallet.getProvider);
      }

      try {
        // First, try to get a Solana-specific provider from Gate Wallet
        let solanaProvider: any = null;

        if (gateWallet) {
          // Try various ways to get Solana provider
          if (typeof gateWallet.getSolanaProvider === 'function') {
            console.log('[GateWalletAdapter] Trying getSolanaProvider()...');
            try {
              solanaProvider = await gateWallet.getSolanaProvider();
              console.log('[GateWalletAdapter] Got Solana provider:', solanaProvider);
            } catch (e) {
              console.log('[GateWalletAdapter] getSolanaProvider failed:', e);
            }
          }

          if (!solanaProvider && typeof gateWallet.getProvider === 'function') {
            console.log('[GateWalletAdapter] Trying getProvider("solana")...');
            try {
              solanaProvider = await gateWallet.getProvider('solana');
              console.log('[GateWalletAdapter] Got provider for solana:', solanaProvider);
            } catch (e) {
              console.log('[GateWalletAdapter] getProvider("solana") failed:', e);
            }
            if (!solanaProvider) {
              try {
                solanaProvider = await gateWallet.getProvider('SOL');
                console.log('[GateWalletAdapter] Got provider for SOL:', solanaProvider);
              } catch (e) {
                console.log('[GateWalletAdapter] getProvider("SOL") failed:', e);
              }
            }
          }

          if (!solanaProvider && gateWallet.solana) {
            console.log('[GateWalletAdapter] Using gateWallet.solana directly');
            solanaProvider = gateWallet.solana;
          }
        }

        // If we got a Solana provider, try signing with it
        if (solanaProvider && typeof solanaProvider.signMessage === 'function') {
          console.log('[GateWalletAdapter] Trying solanaProvider.signMessage...');
          try {
            const response = await solanaProvider.signMessage(message, 'utf8');
            console.log('[GateWalletAdapter] solanaProvider.signMessage response:', response);
            if (response?.signature) {
              return response.signature instanceof Uint8Array ? response.signature : new Uint8Array(Object.values(response.signature));
            }
            if (response instanceof Uint8Array) {
              return response;
            }
          } catch (solProviderError: any) {
            console.log('[GateWalletAdapter] solanaProvider.signMessage failed:', solProviderError);
            if (solProviderError?.code === 4001) {
              throw new Error('Signature request was rejected. Please approve the request in Gate Wallet to continue.');
            }
          }
        }

        // Gate Wallet multi-chain: Try request with network specification first
        if (gateWallet && typeof gateWallet.request === 'function') {
          // Convert message to different formats Gate Wallet might expect
          const messageString = new TextDecoder().decode(message);
          const messageHex = Array.from(message).map(b => b.toString(16).padStart(2, '0')).join('');
          const messageBase64 = btoa(String.fromCharCode(...message));

          console.log('[GateWalletAdapter] Message formats - string:', messageString, 'hex:', messageHex.slice(0, 50) + '...');

          // Try multiple request formats
          // Note: Uint8Array doesn't serialize well to JSON, so we use base64/hex/string formats
          const requestFormats = [
            // Format 1: With network and message as base64
            { method: 'signMessage', params: { message: messageBase64, network: 'SOL', encoding: 'base64' } },
            // Format 2: With network and message as string (plaintext)
            { method: 'signMessage', params: { message: messageString, network: 'SOL' } },
            // Format 3: Solana-specific method with base64
            { method: 'solana_signMessage', params: { message: messageBase64, encoding: 'base64' } },
            // Format 4: With pubkey specified
            { method: 'signMessage', params: { message: messageString, network: 'SOL', pubkey: this._publicKey?.toBase58() } },
            // Format 5: As hex
            { method: 'signMessage', params: { message: messageHex, network: 'SOL', encoding: 'hex' } },
            // Format 6: Array of bytes (as regular array, not Uint8Array)
            { method: 'signMessage', params: { message: Array.from(message), network: 'SOL' } },
            // Format 7: Simple message param as string
            { method: 'signMessage', params: messageString },
          ];

          for (let i = 0; i < requestFormats.length; i++) {
            const format = requestFormats[i];
            console.log(`[GateWalletAdapter] Trying request format ${i + 1}:`, JSON.stringify(format));
            try {
              const response = await gateWallet.request(format);
              console.log(`[GateWalletAdapter] Format ${i + 1} response:`, response, typeof response);

              if (response?.signature) {
                const sig = response.signature;
                if (sig instanceof Uint8Array) return sig;
                if (typeof sig === 'string') {
                  const bs58 = await import('bs58');
                  return bs58.default.decode(sig);
                }
                return new Uint8Array(Object.values(sig));
              }
              if (response instanceof Uint8Array) return response;
              if (typeof response === 'string') {
                const bs58 = await import('bs58');
                return bs58.default.decode(response);
              }
              // If we get here with a response, log it and continue
              console.log(`[GateWalletAdapter] Format ${i + 1} returned unexpected response type`);
            } catch (gateReqError: any) {
              console.log(`[GateWalletAdapter] Format ${i + 1} failed:`, gateReqError);
              console.log(`[GateWalletAdapter] Error details - message:`, gateReqError?.message, 'code:', gateReqError?.code, 'data:', gateReqError?.data);

              // Only throw on explicit user rejection
              if (gateReqError?.code === 4001 ||
                  gateReqError?.message === 'User rejected the request' ||
                  gateReqError?.message === 'User rejected' ||
                  (typeof gateReqError?.message === 'string' && gateReqError.message.toLowerCase() === 'rejected')) {
                throw new Error('Signature request was rejected. Please approve the request in Gate Wallet to continue.');
              }
              // Continue to try next format
            }
          }
        }

        // Try standard signMessage first
        if (typeof wallet.signMessage === 'function') {
          console.log('[GateWalletAdapter] Trying wallet.signMessage...');
          const response = await wallet.signMessage(message);
          console.log('[GateWalletAdapter] wallet.signMessage response:', response);
          return response.signature;
        }

        // Try window.gatewallet.signMessage directly
        if (gateWallet && typeof gateWallet.signMessage === 'function') {
          console.log('[GateWalletAdapter] Trying window.gatewallet.signMessage...');
          try {
            const response = await gateWallet.signMessage(message, 'utf8');
            console.log('[GateWalletAdapter] window.gatewallet.signMessage response:', response);
            if (response?.signature) {
              return response.signature instanceof Uint8Array ? response.signature : new Uint8Array(Object.values(response.signature));
            }
            if (response instanceof Uint8Array) {
              return response;
            }
          } catch (gateError: any) {
            console.log('[GateWalletAdapter] window.gatewallet.signMessage failed:', gateError);
            if (gateError?.message?.includes('rejected') || gateError?.code === 4001) {
              throw new Error('Signature request was rejected. Please approve the request in Gate Wallet to continue.');
            }
            // Continue to try other methods
          }
        }

        // Only try window.solana if it's actually Gate Wallet (not Phantom or other wallet)
        const windowSolana = (window as any).solana;
        if (windowSolana?.isGateWallet && typeof windowSolana.signMessage === 'function') {
          console.log('[GateWalletAdapter] Trying window.solana.signMessage (confirmed Gate Wallet)...');
          try {
            const response = await windowSolana.signMessage(message, 'utf8');
            console.log('[GateWalletAdapter] window.solana.signMessage response:', response);
            if (response?.signature) {
              return response.signature instanceof Uint8Array ? response.signature : new Uint8Array(Object.values(response.signature));
            }
            if (response instanceof Uint8Array) {
              return response;
            }
          } catch (solanaError: any) {
            console.log('[GateWalletAdapter] window.solana.signMessage failed:', solanaError);
            if (solanaError?.message?.includes('rejected') || solanaError?.code === 4001) {
              throw new Error('Signature request was rejected. Please approve the request in Gate Wallet to continue.');
            }
            // Continue to try other methods
          }
        }

        // Try request method on the stored wallet provider with different method names
        const walletAny = wallet as any;
        if (typeof walletAny.request === 'function') {
          const methodsToTry = ['solana_signMessage', 'signMessage', 'personal_sign'];

          for (const methodName of methodsToTry) {
            console.log(`[GateWalletAdapter] Trying wallet.request method: ${methodName}...`);
            try {
              const response = await walletAny.request({
                method: methodName,
                params: {
                  message,
                  display: 'utf8',
                },
              });
              console.log(`[GateWalletAdapter] ${methodName} response:`, response, typeof response);
              if (response?.signature) {
                return response.signature instanceof Uint8Array ? response.signature : new Uint8Array(Object.values(response.signature));
              }
              if (response instanceof Uint8Array) {
                return response;
              }
              if (typeof response === 'object' && response.data) {
                return new Uint8Array(response.data);
              }
              if (typeof response === 'string') {
                const bs58 = await import('bs58');
                return bs58.default.decode(response);
              }
              if (response && typeof response.length === 'number') {
                return new Uint8Array(response);
              }
            } catch (requestError: any) {
              console.log(`[GateWalletAdapter] ${methodName} failed:`, requestError?.message);
              if (requestError?.message?.includes('rejected') || requestError?.code === 4001) {
                throw new Error('Signature request was rejected. Please approve the request in Gate Wallet to continue.');
              }
              // Continue to try next method
            }
          }
        }

        // Try request on window.gatewallet if available
        if (gateWallet && typeof gateWallet.request === 'function') {
          console.log('[GateWalletAdapter] Trying window.gatewallet.request...');
          try {
            const response = await gateWallet.request({
              method: 'signMessage',
              params: { message, display: 'utf8' },
            });
            console.log('[GateWalletAdapter] window.gatewallet.request response:', response);
            if (response?.signature) {
              return response.signature instanceof Uint8Array ? response.signature : new Uint8Array(Object.values(response.signature));
            }
            if (response instanceof Uint8Array) {
              return response;
            }
          } catch (gateReqError: any) {
            console.log('[GateWalletAdapter] window.gatewallet.request failed:', gateReqError);
            if (gateReqError?.message?.includes('rejected') || gateReqError?.code === 4001) {
              throw new Error('Signature request was rejected. Please approve the request in Gate Wallet to continue.');
            }
          }
        }

        // Try direct sign method
        if (typeof walletAny.sign === 'function') {
          console.log('[GateWalletAdapter] Trying sign method...');
          const response = await walletAny.sign(message);
          if (response?.signature) {
            return response.signature;
          }
          if (response instanceof Uint8Array) {
            return response;
          }
        }

        // Try signPersonalMessage (some Solana wallets use this)
        if (typeof walletAny.signPersonalMessage === 'function') {
          console.log('[GateWalletAdapter] Trying signPersonalMessage method...');
          const response = await walletAny.signPersonalMessage(message);
          if (response?.signature) {
            return response.signature;
          }
          if (response instanceof Uint8Array) {
            return response;
          }
        }

        // If we get here, no signing method is available
        // Gate Wallet's Solana signing appears incomplete - the blank popup issue confirms this
        throw new Error(
          'Gate Wallet\'s Solana message signing is not fully supported. ' +
          'While Gate Wallet can display your Solana address, its signing UI for Solana dApps appears incomplete. ' +
          'For authentication and signing, please disconnect and use a Solana-native wallet like Phantom, Solflare, or Backpack. ' +
          'You can still connect Gate Wallet to view your address and participate in the Gate Wallet campaign for bonus points.'
        );
      } catch (error: any) {
        console.error('[GateWalletAdapter] Sign message error:', error);
        throw new WalletSignMessageError(error?.message, error);
      }
    } catch (error: any) {
      this.emit('error', error);
      throw error;
    }
  }

  private _disconnected = () => {
    const wallet = this._wallet;
    if (wallet) {
      wallet.off('disconnect', this._disconnected);
      wallet.off('accountChanged', this._accountChanged);

      this._wallet = null;
      this._publicKey = null;

      this.emit('error', new WalletDisconnectedError());
      this.emit('disconnect');
    }
  };

  private _accountChanged = (newPublicKey?: GateWalletPublicKey | string) => {
    if (!newPublicKey) {
      this._disconnected();
      return;
    }

    const publicKey = this._publicKey;
    if (!publicKey) return;

    try {
      let newPubKey: PublicKey;
      if (typeof newPublicKey === 'string') {
        newPubKey = new PublicKey(newPublicKey);
      } else if (typeof newPublicKey.toBytes === 'function') {
        newPubKey = new PublicKey(newPublicKey.toBytes());
      } else if (typeof newPublicKey.toString === 'function') {
        newPubKey = new PublicKey(newPublicKey.toString());
      } else {
        throw new Error('Invalid public key format');
      }

      if (publicKey.equals(newPubKey)) return;
      this._publicKey = newPubKey;
      this.emit('connect', this._publicKey);
    } catch (error: any) {
      this.emit('error', new WalletPublicKeyError(error?.message, error));
    }
  };
}

// Singleton instance for direct connection
let gateWalletAdapterInstance: GateWalletAdapter | null = null;

export function getGateWalletAdapter(): GateWalletAdapter {
  if (!gateWalletAdapterInstance) {
    gateWalletAdapterInstance = new GateWalletAdapter();
  }
  return gateWalletAdapterInstance;
}

// Helper to check if Gate Wallet is installed
export function isGateWalletInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window as any).gatewallet || (window as any).solana?.isGateWallet;
}

// Gate Wallet download URL
export const GATE_WALLET_DOWNLOAD_URL = 'https://www.gatesite.casa/web3/ref?code=LFyUI5Mz';

// Direct connect function - automatically connects to Gate Wallet
export async function connectGateWallet(): Promise<void> {
  const adapter = getGateWalletAdapter();
  await adapter.connect();
}
